import React, { createContext, useContext, useEffect, useState } from 'react'
import { type UserAccountData } from 'src/utils/helper'
import type { UserProfileData } from '@/helpers/user-profile'
import { getUserProfileDataApi } from 'src/utils/api'

const DEFAULT_PROFILE_IMAGE = '/images/avatars/profile_image_4.webp'

type UserContextType = {
  currentUser: Partial<UserAccountData> | null
  profileData: UserProfileData | null
  isLoginComplete: boolean
  loginUser: (userData: any) => void
  setReferralStatistics: (stats: {
    betCount: string
    betVolume: string
    userCount: string
  }) => void
  setProfilePictureData: (profileImage: string) => void
  setLoginCompleted: (completed: boolean) => void
  logoutUser: () => void
  isUserConnecting: boolean
  setUserConnecting: () => void
  fetchProfileData: (walletAddress: string) => Promise<void>
  isProfileLoading: boolean
}

const UserContext = createContext<UserContextType>(null as any)

export const useUserContext = () => useContext(UserContext)

const getInitialState = () => {
  if (typeof window !== 'undefined') {
    const currentUser = localStorage.getItem('currentUser')
    return currentUser ? JSON.parse(currentUser) : null
  }
  return null
}

const getInitialLoginComplete = () => {
  if (typeof window !== 'undefined') {
    const isLoginComplete = localStorage.getItem('isLoginComplete')
    return isLoginComplete ? JSON.parse(isLoginComplete) : false
  }
  return null
}

export const UserProvider: React.CFC = ({ children }) => {
  const [currentUser, setCurrentUser] =
    useState<Partial<UserAccountData> | null>(getInitialState)
  const [profileData, setProfileData] = useState<UserProfileData | null>(null)
  const [isProfileLoading, setIsProfileLoading] = useState(false)

  const [isLoginComplete, setIsLoginCompleted] = useState<boolean>(
    getInitialLoginComplete
  )

  const [isUserConnecting, setIsUserConnecting] = useState<boolean>(false)

  useEffect(() => {
    localStorage.setItem('currentUser', JSON.stringify(currentUser))
  }, [currentUser])

  useEffect(() => {
    localStorage.setItem('isLoginComplete', JSON.stringify(isLoginComplete))
  }, [isLoginComplete])

  const fetchProfileData = async (walletAddress: string) => {
    if (!walletAddress) {
      return
    }

    try {
      setIsProfileLoading(true)
      const response = await getUserProfileDataApi({
        wallet_address: walletAddress,
      })
      setProfileData(response?.data?.user)
    } catch (error) {
      console.error('Error fetching profile data:', error)
    } finally {
      setIsProfileLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser?.wallet_address) {
      fetchProfileData(currentUser.wallet_address)
    }
  }, [currentUser?.wallet_address])

  const setLoginCompleted = (completed: boolean) => {
    setIsUserConnecting(false)
    setIsLoginCompleted(completed)
  }

  const setUserConnecting = () => {
    setIsUserConnecting(true)
  }

  const loginUser = (userData: Partial<UserAccountData>) => {
    setCurrentUser(userData)
  }

  const setReferralStatistics = (stats: {
    betCount: string
    betVolume: string
    userCount: string
  }) => {
    setCurrentUser(prevState => ({
      ...prevState,
      referralStatistics: stats,
    }))
  }

  const setProfilePictureData = (profile_image: string) => {
    setCurrentUser(prevState => ({
      ...prevState,
      profile_image: profile_image || DEFAULT_PROFILE_IMAGE,
    }))
  }

  const logoutUser = () => {
    setCurrentUser(null)
    setProfileData(null)
    setLoginCompleted(false)
  }

  return (
    <UserContext.Provider
      value={{
        currentUser,
        profileData,
        loginUser,
        setReferralStatistics,
        setProfilePictureData,
        logoutUser,
        isLoginComplete,
        setLoginCompleted,
        isUserConnecting,
        setUserConnecting,
        fetchProfileData,
        isProfileLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
